import { personalService } from '@/services/PersonalService'
import { licenseDeviceApi, type LicenseDeviceResponse } from '@/services/licenseDeviceApi'
import { licenseDatabaseApi, licenseSqlInstanceApi } from '@/services/licenseSqlInstanceApi'
import { licenseAdminApi } from '@/services/licenseAdminApi'
import { subscriptionTenantApi } from '@/services/subscriptionTenantApi'
import type { TenantSubscriptionDetailResponse } from '@/services/subscriptionTenantTypes'
import { userService } from '@/services/UserService'
import type { ViewUserDetailsResponse } from '@/helpers/api/WebApiClient'

export type DashboardActivityItem = {
	id: string
	text: string
	at: string
	sortKey: number
}

export type CustomerPortalDashboardVm = {
	profile: ViewUserDetailsResponse
	subscription: TenantSubscriptionDetailResponse
	devices: LicenseDeviceResponse[]
	activeUserCount: number
	activeDeviceCount: number
	activities: DashboardActivityItem[]
	trialTotalDays: number
	daysRemaining: number
	activeInstances: number
	activeDatabases: number
}

export async function loadCustomerPortalDashboard(): Promise<CustomerPortalDashboardVm> {
	const [subscription, devices, activeSqlInstanceCount, activeDatabaseCount, users, profile, audit] = await Promise.all([
		subscriptionTenantApi.getCurrentSubscriptionDetail(),
		licenseDeviceApi.list().catch(() => [] as LicenseDeviceResponse[]),
		licenseSqlInstanceApi.getActiveCount().catch(() => 0),
		licenseDatabaseApi.getActiveCount().catch(() => 0),
		userService.getList().catch(() => [] as ViewUserDetailsResponse[]),
		personalService.getProfile(),
		licenseAdminApi.searchAudit({ pageNumber: 1, pageSize: 6 }).catch(() => ({ data: [] })),
	])

	const activeUserCount = subscription.activeUserCount ?? users.filter((u) => u.isActive).length
	const activeDeviceCount = devices.filter((d) => d.isActive).length

	const daysRemaining = subscription.isTrial
		? Math.max(0, subscription.trialDaysRemaining ?? 0)
		: Math.max(0, Math.ceil((new Date(subscription.expiredOn).getTime() - Date.now()) / 86400000))

	const trialTotalDays = subscription.isTrial ? Math.max(14, daysRemaining) : 0

	return {
		profile,
		subscription,
		devices,
		activeUserCount,
		activeDeviceCount,
		activities: (audit.data ?? []).map((item) => ({
			id: `audit-${item.id}`,
			text: item.summary || item.eventType,
			at: item.createdOn,
			sortKey: new Date(item.createdOn).getTime(),
		})),
		trialTotalDays,
		daysRemaining,
		activeInstances: activeSqlInstanceCount,
		activeDatabases: subscription.activeDatabaseCount ?? activeDatabaseCount,
	}
}
